import { useState, useEffect } from 'react'
import { listBillingUsage } from '../services/billing-api'
import type { BillingUsageItem } from '../types/billing.types'

interface UseBillingState {
  items: BillingUsageItem[]
  loading: boolean
  error: Error | null
  refresh: () => void
}

export function useBilling(period: string): UseBillingState {
  const [items, setItems] = useState<BillingUsageItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!period) return

    let cancelled = false

    const fetchItems = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await listBillingUsage(period)
        if (!cancelled) {
          setItems(data)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch billing usage'))
          setLoading(false)
        }
      }
    }

    fetchItems()

    return () => {
      cancelled = true
    }
  }, [period, refreshKey])

  const refresh = () => {
    setRefreshKey(prev => prev + 1)
  }

  // With no period there is nothing to fetch; derive the empty state rather than
  // setting it inside the effect (avoids a synchronous setState / cascading render).
  if (!period) {
    return { items: [], loading: false, error: null, refresh }
  }

  return { items, loading, error, refresh }
}
